/**
 * @define FormValidation
 * @author pierre-michel@on-running.com
 *
 * To add client-side form validations. It works using the .field wrapper
 * appending the new error element '.form-validation-error' to it (a red block
 * below the input will slide down).
 *
 * As simple as:
 * new On.FormValidation({ $form: $('#doesnt_have_to_be_a_form_tag') });
 *
 * All fields ('input:not(:checkbox):not([name$="[id]"]), select, textarea')
 * within the form are required by default. Add the class 'js-is-optional' to
 * the input to make it optional or use the 'isRequired' param to add
 * conditions.
 *
 * Make sure to set the error messages needed for the form in the html.
 * See 'errorMsg' parameter for more details:
 * <script>
 *   On.FormValidation.setErrorMessages({
 *     required: "<%= t('errors.required_field') %>",
 *     invalid: "<%= t('errors.invalid_field') %>",
 *   });
 * </script>
 *
 */
import $ from 'jquery'

window.On.FormValidation = (function () {
  class FormValidation {
    constructor(params) {
      const defaultParams = {
        $form: $(/* form or wrapper element */),
        $globalError: $(/* element to show/hide with global error message */),
        // or add .form-validation-error--global to the .field
        fieldErrorClass: 'field--error field--soft-validation',
        isRequired: {
          /**
           * @define isRequired
           *
           * "shoe_size" is the value specified in data-validate-ref
           * shoe_size($form, $field) {
           *   return $form.find('.product-type').value == 'shoes';
           * }
           */
        },
        isInvalid: {
          /**
           * @define isInvalid
           *
           * "email" is the value specified in data-validate-ref
           * email($form, $field) {
           *   return /.+@.+\..+/.test($field.val());
           * }
           */
        },
        value: {
          /**
           * @define value
           *
           * "calendar" is the value specified in data-validate-ref
           * calendar($form, $field) {
           *   return $form.find('.calendar-hidden-input').val();
           * }
           */
        },
        errorMsg: FormValidation.errorMsg,
        /**
         * @define errorMsg
         * Can be set using On.FormValidation.setErrorMessages()
         *
         * Can also be an object to be more specific
         * {
         *   required: 'This field is required.',
         *   invalid: 'This field is not valid.',
         *
         *   "email" is the value specified in data-validate-ref
         *   email: {
         *     invalid: 'Please enter a valid email address.'
         *   }
         * }
         */
        groups: [
          /**
           * @define groups
           *
           * To specify the groups to consider (fields with the same
           * data-validate-group attribute value, fields that need to be
           * validated together)
           */
        ],
        validateOnSubmit: true, // $form has to be a form tag
        onError: () => {},
        onSubmit: () => {},
        onAjaxComplete: null, // If defined, the default submit event is prevented
      }
      Object.assign(this, defaultParams, params)
      this.init()
    }

    init() {
      if (!this.$form.jquery) {
        this.$form = $(this.$form)
      }
      if (this.$form[0].formValidationInitiated) {
        return
      }
      this.id = On.FormValidation.generateId()
      On.FormValidation.ids.push(this.id)

      this.firstValidationDone = false
      this.fieldsOkay = []
      this.fieldsError = []
      this.proceed = true
      this.$form[0].formValidationInitiated = true
      this.$form[0].FormValidation = this
      FormValidation.fieldErrorClass = this.fieldErrorClass
      this.attachSubmitEvent()
    }

    attachSubmitEvent() {
      if (!this.validateOnSubmit || !this.$form.is('form')) {
        return
      }
      this.$form.attr('onsubmit', 'return this.FormValidation.submit()')
    }

    addLoadingStateToButton() {
      this.addLoadingStateTimeout = setTimeout(() => {
        this.$form
          .find('.js-form-validation-cta')
          .addClass('cta--loading')
          .attr('disabled', 'disabled')
      }, 200)
    }

    removeLoadingStateFromButton() {
      clearTimeout(this.addLoadingStateTimeout)
      this.$form.find('.js-form-validation-cta').removeClass('cta--loading').removeAttr('disabled')
    }

    submitAjax() {
      if (!this.onAjaxComplete) {
        return
      }
      if (this.xhr) {
        this.xhr.abort()
      }
      const formData = {}
      this.$form.serializeArray().map(obj => (formData[obj.name] = obj.value))

      this.xhr = $.ajax({
        type: this.$form[0].method,
        url: this.$form[0].action,
        data: formData,
        dataType: 'json',

        success: json => {
          this.onAjaxComplete(this.$form, json)
        },
        complete: () => {
          this.removeLoadingStateFromButton()
        },
      })
    }

    submit() {
      const valid = this.validate()
      if (valid) {
        this.addLoadingStateToButton()
        this.submitAjax()
      }
      return valid && !this.onAjaxComplete
    }

    validate(dontShowErrorMsg, instant) {
      const self = this
      self.fieldsOkay = []
      self.fieldsError = []
      self.proceed = true

      FormValidation.getFields(this.$form).each(function () {
        self.validateField($(this))
      })
      if (!self.proceed && !dontShowErrorMsg) {
        self.resetFieldStates(instant)
      }
      self.toggleGlobalErrorMsg(instant)
      self[self.proceed ? 'onSubmit' : 'onError'](self.$form)
      self.firstValidationDone = true
      return self.proceed
    }

    toggleGlobalErrorMsg(instant, noFlash) {
      if (!this.$globalError.length) {
        this.$globalError = this.$form.find('.form-validation-error--global')
      }
      if (!this.proceed) {
        FormValidation.showErrorMsg(this.$globalError, instant, noFlash)
      } else {
        FormValidation.hideErrorMsg(this.$globalError, instant)
      }
    }

    getValue($field) {
      if ($field.is('input[type="radio"]')) {
        return this.getRadioValue($field)
      }
      const ref = $field.closest('.field').attr('data-validate-ref')
      return this.value[ref] ? this.value[ref]() : $field.val()
    }

    getRadioValue($field) {
      const $checked = $field.closest('.field').find(`input[name="${$field.attr('name')}"]:checked`)
      return $checked.length ? $checked.val() : ''
    }

    errorInvalidFields($field, isInvalid) {
      const isEmpty = FormValidation.fieldIsEmpty($field, this)
      if (isEmpty || isInvalid) {
        this.fieldsError.push($field)
        this.proceed = false
      } else {
        this.fieldsOkay.push($field)
      }
      return isEmpty || isInvalid
    }

    validateField($field) {
      const fieldWrapper = $field.closest('.field')
      const ref = fieldWrapper.attr('data-validate-ref')
      const isRequired = this.isRequired[ref]
        ? this.isRequired[ref](this.$form, $field)
        : !$field.is('.js-is-optional')

      const isInvalid = this.isInvalid[ref] ? this.isInvalid[ref](this.$form, $field) : false

      $field[0].isInvalid = isInvalid
      var isValid = !isInvalid

      if (isRequired || isInvalid) {
        isValid = !this.errorInvalidFields($field, isInvalid)
      } else {
        this.fieldsOkay.push($field)
      }
      return { isRequired, isValid }
    }

    removeErrorMsg($field, instant, callback) {
      const $errorMsg = this.getErrorMsgElement($field)
      FormValidation.hideErrorMsg($errorMsg, instant, callback)

      this.fieldsError = this.fieldsError.filter($fieldError => $fieldError[0] != $field[0])
    }

    toggleErrorIfValid($field, instant, noFlash) {
      if (this.validateField($field).isValid) {
        this.removeErrorMsg($field, instant)
      } else {
        const $errorMsg = this.getErrorMsgElement($field).html(this.getErrorMsg($field))
        FormValidation.showErrorMsg($errorMsg, instant, noFlash)
      }
      this.proceed = !this.fieldsError.length
      this.toggleGlobalErrorMsg(instant, noFlash)
    }

    resetFieldStates(instant) {
      this.fieldsOkay.map($field => {
        this.detachErrorCheckToField($field)
        this.removeErrorMsg($field, instant, () => {
          this.getErrorMsgElement($field).remove()
        })
      })
      this.fieldsError.map($field => {
        this.attachErrorCheckToField($field, instant)
        FormValidation.showErrorMsg(this.getErrorMsgElement($field), instant)
      })
    }

    getErrorMsg($field) {
      if (typeof this.errorMsg == 'string') {
        return this.errorMsg
      }
      const ref = $field.closest('.field').attr('data-validate-ref')
      const key = $field[0].isInvalid ? 'invalid' : 'required'
      const errorMsgObj = this.errorMsg[ref] || this.errorMsg

      return (
        errorMsgObj[key] || this.errorMsg[key] || errorMsgObj.required || this.errorMsg.required
      )
    }

    getErrorMsgElement($field) {
      return $field.closest('.field').find('.form-validation-error')
    }

    getErrorCheckEventName($field) {
      const eventName = $field.is('input[type="radio"]') ? 'click' : 'change'
      return `${eventName}.fielderror`
    }

    attachErrorCheckToField($field, instant) {
      var $errorMsg = this.getErrorMsgElement($field)
      if (!$errorMsg.length) {
        $errorMsg = $('<div></div>')
          .css('display', 'none')
          .addClass('form-validation-error')
          .html(this.getErrorMsg($field))
          .appendTo($field.closest('.field'))
      } else {
        $errorMsg.html(this.getErrorMsg($field))
      }
      if ($field[0][`hasErrorCheckEvent${this.id}`]) {
        return
      }
      const eventName = this.getErrorCheckEventName($field)
      $field.off(eventName).on(eventName, () => {
        const self = this
        const noFlash = true
        this.toggleErrorIfValid($field, instant, noFlash)
        this.getGroupMembers($field).each(function () {
          self.toggleErrorIfValid($(this), instant, noFlash)
        })
      })
      On.FormValidation.ids.map(id => ($field[0][`hasErrorCheckEvent${id}`] = false))

      $field[0][`hasErrorCheckEvent${this.id}`] = true
      this.linkValidationEventOfGroup($field, instant)
    }

    detachErrorCheckToField($field) {
      $field.off(this.getErrorCheckEventName($field))
      $field[0][`hasErrorCheckEvent${this.id}`] = false
    }

    getGroupMembers($field) {
      const $thisFieldWrapper = $field.closest('.field')
      const group = $thisFieldWrapper.attr('data-validate-group')
      var $members = $()

      const $fieldWrappers = !group
        ? $thisFieldWrapper
        : group.indexOf(this.groups) < 0
        ? $()
        : this.$form.find(`.field[data-validate-group="${group}"]`)

      $fieldWrappers.each(function () {
        $members = $members.add($(this).find(FormValidation.inputSelector).not($field))
      })
      return $members
    }

    linkValidationEventOfGroup($field, instant) {
      const self = this
      this.getGroupMembers($field).each(function () {
        self.attachErrorCheckToField($(this), instant)
      })
    }
  }

  // STATIC FUNCTIONS

  Object.assign(FormValidation, {
    ids: [],
    inputSelector:
      'input:enabled' +
      ':not([type="hidden"]):not([type="checkbox"]):not([name$="[id]"]), ' +
      'select:enabled, textarea:enabled', // also used within querySelector()
    errorMsg: {
      required: 'This field is required.',
      invalid: 'This field is not valid.',
    },

    getFields($form) {
      var $fields = $()
      $form.find('.field:not(.js-form-validation-skip-field)').each(function () {
        $fields = $fields.add($(this).find(FormValidation.inputSelector))
      })
      return $fields
    },

    fieldIsEmpty($field, instance, testMethodName = 'some') {
      const inst = instance || FormValidation.getClosestInstance($field)

      return $field
        .closest('.field')
        .find(FormValidation.inputSelector)
        .toArray()
        [testMethodName](field => !/\S/.test(inst.getValue($(field))))
    },

    fieldIsIncomplete($field) {
      return FormValidation.fieldIsEmpty($field) && !FormValidation.fieldIsEmpty($field, 0, 'every')
    },

    getClosestInstance($elem) {
      let $element = $elem
      if (!$element.length) {
        return
      }
      const notInstance = (i, el) => !el.FormValidation && el != document.body
      while ($element.is(notInstance)) {
        $element = $element.parent()
      }
      return $element[0].FormValidation
    },

    showErrorMsg($errorMsg, instant, noFlash) {
      $errorMsg.closest('.field').addClass(this.fieldErrorClass)
      if (instant) {
        $errorMsg.css('display', 'block')
        return
      }
      if (!noFlash && $errorMsg.is(':visible')) {
        $errorMsg.fadeTo(50, 0.5, () => {
          $errorMsg.fadeTo(80, 1)
        })
      } else {
        $errorMsg.stop(true).slideDown(300)
      }
    },

    hideErrorMsg($errorMsg, instant, callback) {
      $errorMsg.closest('.field').removeClass(this.fieldErrorClass)
      if (instant) {
        $errorMsg.css('display', 'none')
        callback()
      } else {
        $errorMsg.stop(true).slideUp({
          duration: 300,
          complete: callback,
        })
      }
    },

    useAjaxToSubmit(form) {
      return form.FormValidation && form.FormValidation.onAjaxComplete
    },

    generateId() {
      return (this.latestId = !this.latestId ? 1 : ++this.latestId)
    },

    setErrorMessages(settings) {
      Object.assign(FormValidation.errorMsg, settings)
    },
  })

  return FormValidation
})()
